import { useEffect, useRef } from 'react';
import autosize from 'autosize';
import styled from 'styled-components';
import { FlexAlignCenter, FlexCenterBoth } from 'components/primitives/flex';
import { useMutation, useQueryClient } from 'react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useBreakpoint } from 'components/BreakpointProvider';
import { mediaMMdMin } from 'helpers/breakpoints';
import { getUserTypeColor } from 'utils/colors';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import OpenApiConfiguration from 'api/OpenApiConfiguration';
import { DomesticV2ConversationsConversationIdMessagesPostRequest } from 'swagger/models';
import { ConversationsApi } from 'swagger/apis/conversations-api';
import { useUserDetails } from 'auth/mutations/userHooks';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import { useAdminMessageRecipientSelect } from './hooks/useAdminView';
import { messagesFilterOptions } from './constants';
import { activeConversationIdAtom, activeTabNameAtom, messagesFilterAtom, sentSpamMessageAtom } from './store';
import useConversationDetail from './hooks/useConversationDetail';

const conversationsApi = new ConversationsApi(OpenApiConfiguration);

const scrollToBottomOfPage = () => window.scrollTo(0, document.body.scrollHeight);

export default function MessageInputBar() {
  const { enqueueSnackbar } = useLazySnackbar();
  const textAreaRef = useRef<any>(null);
  const queryClient = useQueryClient();
  const breakpoints = useBreakpoint();
  const setFilterValue = useSetAtom(messagesFilterAtom);
  const activeConversationId = useAtomValue(activeConversationIdAtom);
  const smallScreen = breakpoints.xs || breakpoints.sm;
  const activeTabName = useAtomValue(activeTabNameAtom);
  const { adminMessageRecipient, adminMessageRecipientSelect } = useAdminMessageRecipientSelect();
  const setSentSpamMessage = useSetAtom(sentSpamMessageAtom);
  const { data: conversation } = useConversationDetail();
  const { userDetails } = useUserDetails();

  const sendMessageMutation = useMutation(
    ({
      conversationId,
      body,
    }: {
      conversationId: string;
      body: DomesticV2ConversationsConversationIdMessagesPostRequest;
    }) => conversationsApi.domesticV2ConversationsConversationIdMessagesPost(conversationId, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['conversationDetail', activeConversationId]);
        setFilterValue(messagesFilterOptions.ALL);
      },
      onError: ({ response: { data = {} } }: AxiosError, { body: { body } }) => {
        const resMessage = (data as any).data?.message;
        if (resMessage && resMessage?.[0] === 'contained spam') {
          let censoredMessage = body;

          resMessage?.[1]?.entities?.forEach(({ entity }) => {
            censoredMessage = censoredMessage.replace(entity, entity.replace(/./g, '*'));
          });

          setSentSpamMessage({
            conversation_id: conversation.booking.conversation_id,
            body: censoredMessage,
            sender: {
              id: userDetails.id,
              user_type: conversation?.my_user_type_in_conversation,
              first_name: userDetails.first_name,
              picture: {
                url: userDetails.picture.url,
              },
            },
            created_at: dayjs().toISOString(),
          });

          return;
        }

        enqueueSnackbar('Message not sent. Try again later.', { variant: 'error', autoHideDuration: 4000 });
      },
    }
  );

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
      autosize(textAreaRef.current);
    }
  }, [textAreaRef.current]);

  const handleSendMessage = async () => {
    if (textAreaRef.current.value) {
      setSentSpamMessage({});
      sendMessageMutation.mutate({
        conversationId: activeConversationId,
        body: {
          body: textAreaRef.current.value,
          admin_recipient: adminMessageRecipient,
        },
      });
      textAreaRef.current.value = '';
      autosize.update(textAreaRef.current);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && e.shiftKey === false && !smallScreen) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Container>
      <InputBar onClick={scrollToBottomOfPage}>
        <Textarea ref={textAreaRef} placeholder="Message" rows={1} onKeyDown={onKeyDown} />
        {adminMessageRecipientSelect}
        <SendMessageButton
          onClick={handleSendMessage}
          onKeyDown={onKeyDown}
          $bgColor={getUserTypeColor(activeTabName)}
          data-testid="inbox-message-send-button"
        >
          <ArrowUp />
        </SendMessageButton>
      </InputBar>
    </Container>
  );
}

const Container = styled.div`
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
  padding: 16px;
  background: #fff;

  ${mediaMMdMin} {
    padding: 16px 30px;
  }
`;

const InputBar = styled(FlexAlignCenter)`
  border: 1px solid #dbdfe5;
  border-radius: 32px;
  padding: 7px 7px 7px 17px;
`;

const SendMessageButton = styled(FlexCenterBoth)<{ $bgColor: string }>`
  width: 24px;
  height: 24px;
  background: ${({ $bgColor }) => $bgColor};
  color: #fff;
  border-radius: 50%;
  cursor: pointer;
`;

const Textarea = styled.textarea`
  flex: 1;
  min-height: 26px;
  max-height: 106px;
  line-height: 1.25;
  border: none;
  outline: none;
  resize: none !important;
  padding: 3px 3px 3px 0;
`;

const ArrowUp = (props) => (
  <svg width={12} height={12} fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M5.967 1v10m0-10 4 4m-4-4-4 4"
      stroke="#fff"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
